import React from 'react';
import { colors, spacing, borderRadius, shadows, typography } from '../../styles/designTokens';

/**
 * StatCard Component
 * 
 * Displays a key metric with trend indicator for dashboard hero section.
 * Supports gamification elements like rank badges and progress indicators.
 * 
 * Usage:
 * <StatCard
 *   value={42}
 *   label="Leads Generated"
 *   trend={{ value: 5, direction: 'up' }}
 *   icon="🎯"
 *   variant="primary"
 * />
 */

const StatCard = ({
  value,
  label,
  trend = null,
  icon = null,
  variant = 'default', // 'default' | 'primary' | 'success' | 'warning' | 'gold' | 'silver' | 'bronze'
  subtitle = null,
  onClick = null,
  className = ''
}) => {
  const variantStyles = {
    default: {
      bg: '#ffffff',
      border: colors.neutral[200],
      iconBg: colors.neutral[100],
      iconColor: colors.neutral[600]
    },
    primary: {
      bg: colors.primary[50],
      border: colors.primary[200] || colors.primary.light,
      iconBg: colors.primary[100],
      iconColor: colors.primary.main
    },
    success: {
      bg: colors.success[50],
      border: colors.success[200] || colors.success.light,
      iconBg: colors.success[100],
      iconColor: colors.success.main
    },
    warning: {
      bg: colors.warning[50],
      border: colors.warning[200] || colors.warning.light,
      iconBg: colors.warning[100],
      iconColor: colors.warning.main
    },
    gold: {
      bg: colors.rank.gold.bg,
      border: colors.rank.gold.border,
      iconBg: '#FEF3C7',
      iconColor: colors.rank.gold.text
    },
    silver: {
      bg: colors.rank.silver.bg,
      border: colors.rank.silver.border,
      iconBg: colors.neutral[200],
      iconColor: colors.rank.silver.text
    },
    bronze: {
      bg: colors.rank.bronze.bg,
      border: colors.rank.bronze.border,
      iconBg: '#FFEDD5',
      iconColor: colors.rank.bronze.text
    }
  };

  const style = variantStyles[variant] || variantStyles.default;

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.sm,
        padding: spacing.md,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease'
      }}
      className={`${onClick ? 'hover:shadow-md' : ''} ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Label */}
          <p 
            style={{ 
              fontSize: typography.small.size,
              color: colors.neutral[600],
              marginBottom: spacing.xs
            }}
          >
            {label}
          </p>
          
          {/* Value */}
          <p 
            style={{ 
              fontSize: typography.metric.secondary.size,
              fontWeight: typography.metric.secondary.weight,
              color: colors.neutral[900],
              lineHeight: 1.1
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          
          {/* Subtitle */}
          {subtitle && (
            <p 
              style={{ 
                fontSize: typography.xs.size,
                color: colors.neutral[500],
                marginTop: spacing.xs
              }}
            >
              {subtitle}
            </p>
          )}
          
          {/* Trend Indicator */}
          {trend && (
            <TrendIndicator 
              value={trend.value} 
              direction={trend.direction}
              label={trend.label}
            />
          )}
        </div>
        
        {/* Icon */}
        {icon && (
          <div
            style={{
              backgroundColor: style.iconBg,
              borderRadius: borderRadius.md,
              padding: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem'
            }}
          >
            {typeof icon === 'string' ? icon : icon}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * TrendIndicator - Shows direction and change amount
 */
const TrendIndicator = ({ value, direction, label }) => {
  const isUp = direction === 'up';
  const isNeutral = direction === 'neutral' || value === 0;
  
  const trendColor = isNeutral 
    ? colors.neutral[500]
    : isUp 
      ? colors.success.main 
      : colors.error.main;

  return (
    <div 
      className="flex items-center gap-1"
      style={{ marginTop: spacing.xs }}
    >
      {!isNeutral && (
        <svg 
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill={trendColor}
          style={{ transform: isUp ? 'none' : 'rotate(180deg)' }}
        >
          <path d="M6 2L10 8H2L6 2Z" />
        </svg>
      )}
      <span 
        style={{ 
          fontSize: typography.xs.size,
          fontWeight: 500,
          color: trendColor
        }}
      >
        {isUp ? '+' : ''}{value !== undefined ? value : ''}
        {label && <span style={{ color: colors.neutral[500], fontWeight: 400 }}> {label}</span>}
      </span>
    </div>
  );
};

/**
 * RankBadge - Displays rank position with medal styling
 */
export const RankBadge = ({ rank, size = 'md' }) => {
  const sizes = {
    sm: { width: '24px', height: '24px', fontSize: '12px' },
    md: { width: '32px', height: '32px', fontSize: '14px' },
    lg: { width: '40px', height: '40px', fontSize: '18px' }
  };

  const rankStyles = {
    1: { bg: colors.rank.gold.border, color: '#92400E' },
    2: { bg: colors.rank.silver.border, color: '#374151' },
    3: { bg: colors.rank.bronze.border, color: '#9A3412' }
  };

  const style = rankStyles[rank] || { 
    bg: colors.neutral[200], 
    color: colors.neutral[700] 
  };

  return (
    <div
      style={{
        ...sizes[size],
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        boxShadow: rank <= 3 ? shadows.sm : 'none'
      }}
    >
      {rank}
    </div>
  );
};

/**
 * ProgressToNextRank - Shows gap to next position
 */
export const ProgressToNextRank = ({ 
  currentValue, 
  targetValue, 
  targetRank,
  metric = 'leads'
}) => {
  const gap = targetValue - currentValue;
  const progress = targetValue > 0 ? (currentValue / targetValue) * 100 : 0;
  
  if (gap <= 0) return null;

  return (
    <div 
      style={{ 
        backgroundColor: colors.primary[50],
        borderRadius: borderRadius.md,
        padding: spacing.sm,
        marginTop: spacing.sm
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span style={{ fontSize: typography.xs.size, color: colors.neutral[600] }}>
          {gap} more {metric} to reach #{targetRank}
        </span>
        <span style={{ fontSize: typography.xs.size, fontWeight: 600, color: colors.primary.main }}>
          {Math.round(progress)}%
        </span>
      </div>
      <div 
        style={{ 
          height: '4px',
          backgroundColor: colors.neutral[200],
          borderRadius: borderRadius.full,
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(progress, 100)}%`,
            backgroundColor: colors.primary.main,
            borderRadius: borderRadius.full,
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </div>
  );
};

/**
 * Skeleton loader for StatCard
 */
export const StatCardSkeleton = () => (
  <div
    style={{
      backgroundColor: '#ffffff',
      border: `1px solid ${colors.neutral[200]}`,
      borderRadius: borderRadius.lg,
      boxShadow: shadows.sm,
      padding: spacing.md
    }}
  >
    <div className="animate-pulse">
      <div 
        style={{ 
          height: '14px', 
          width: '60%', 
          backgroundColor: colors.neutral[200],
          borderRadius: borderRadius.sm,
          marginBottom: spacing.sm
        }} 
      />
      <div 
        style={{ 
          height: '28px', 
          width: '40%', 
          backgroundColor: colors.neutral[200],
          borderRadius: borderRadius.sm
        }} 
      />
    </div>
  </div>
);

export default StatCard;





